import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BiShow } from "react-icons/bi";
import { CgSpinner } from "react-icons/cg";
import { useSelector } from "react-redux";
import useServerPagination from "../../../../hooks/useServerPagination";
import PaginationControls from "../../../../components/SmartDataTable/PaginationControls";
import SearchFilter from "../../../../components/SmartDataTable/SearchFilter";
import { checkAccessFn } from "../../../../utils/checkAccess";

const NAV_BASE = "p-2 px-4 rounded-md text-white text-sm font-medium";
const NAV_ACTIVE = { backgroundColor: "#ef4444" };
const navLink = (isActive) => ({ className: NAV_BASE, style: isActive ? NAV_ACTIVE : { backgroundColor: "#1f2937" } });

export default function ListDataProject({ titles = [] }) {
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);

  const token = useMemo(() => localStorage.getItem("nodeToken"), []);
  const [searchTerm, setSearchTerm] = useState("");

  const { data, paginationState, goToPage, changePageSize } = useServerPagination({
    apiEndpoint: "/dashboard/projects",
    token,
    defaultPageSize: 50,
    searchTerm,
    searchDebounceMs: 300,
  });

  return (
    <div className="p-2 uppercase">

      {/* Toolbar */}
      <div className="flex flex-row-reverse flex-wrap items-center w-full gap-2 mb-4">
        <SearchFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search projects..."
          isLoading={paginationState.isLoading && searchTerm.length > 0}
        />
        {checkAccess("Project", "project-create") && (
          <Link to="create" className="p-2 mr-auto text-sm font-medium text-white bg-red-600 rounded-md">
            ADD NEW PROJECT +
          </Link>
        )}
      </div>

      {/* Scrollable Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
        <table className="min-w-full">
          <thead className="sticky top-0 z-20 bg-white border-b border-slate-200">
            <tr>
              {titles.map((title, i) => (
                <th key={i} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {paginationState.isLoading ? (
              <tr>
                <td colSpan={titles.length} className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <CgSpinner className="animate-spin text-xl text-blue-500" />
                    <span>Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={titles.length} className="px-6 py-8 text-center text-gray-400">
                  {searchTerm ? `No results found for "${searchTerm}"` : "No Data Found"}
                </td>
              </tr>
            ) : data.map((item, index) => (
              <tr key={item.id || index} className="hover:bg-gray-50">
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                  {(paginationState.currentPage - 1) * paginationState.pageSize + index + 1}
                </td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{item.project_id}</td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{item?.quotation?.quotation_number}</td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{item.name}</td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{item?.quotation_type?.name}</td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{item?.company?.name}</td>
                <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{item?.client?.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {checkAccess("Project", "project-list") ? (
                    <Link to={`${item.id}`}>
                      <button className="flex items-center justify-center bg-white border border-gray-400 rounded-md" style={{ width: "2rem", height: "2rem" }}>
                        <BiShow size={"1.2rem"} />
                      </button>
                    </Link>
                  ) : (
                    <span className="text-gray-400">--</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="mt-2">
        <PaginationControls
          currentPage={paginationState.currentPage}
          totalPages={paginationState.totalPages}
          pageSize={paginationState.pageSize}
          totalRecords={paginationState.totalRecords}
          pageSizeOptions={[50, 100, 500, 1000]}
          onPageChange={goToPage}
          onPageSizeChange={changePageSize}
          isLoading={paginationState.isLoading}
        />
      </div>
    </div>
  );
}
